// src/lib/chartAxis.ts (v2.10)
// 月次推移系 chart 共通の x 軸設定
//
// 用途: 縦に並ぶ複数 chart で「月の位置をぴったり重ねる」ためには
//   - 同じ x domain
//   - 同じ marginLeft / marginRight
//   - 同じ tickFormat
//   を共有する必要がある。
//
// 表示形式 (v2.10):
//   - 月 tick は数字のみ ("4" "5" ... "12" "1" "2" "3"、 回転なし)
//   - 年は別の text mark (axis 下段) で 4月 / 1月の位置にのみ薄色表示
import * as Plot from "@observablehq/plot";

// 共通 margin (横幅 1100px の chart 想定)
export const TIME_X_MARGIN_LEFT = 70;
export const TIME_X_MARGIN_RIGHT = 60;

/** Date を月数字に: "4", "5", ..., "12", "1" など (年は別 mark で表示) */
export function fmtMonthJp(d: Date): string {
  return String(d.getUTCMonth() + 1);
}

/** domain ([min,max] の 2要素配列) から月初の Date 配列を生成 */
function buildMonthlyTicks(domain: Date[]): Date[] {
  if (domain.length < 2) return domain;
  const ticks: Date[] = [];
  const cur = new Date(Date.UTC(domain[0].getUTCFullYear(), domain[0].getUTCMonth(), 1));
  const end = new Date(Date.UTC(domain[1].getUTCFullYear(), domain[1].getUTCMonth(), 1));
  while (cur <= end) {
    ticks.push(new Date(cur));
    cur.setUTCMonth(cur.getUTCMonth() + 1);
  }
  return ticks;
}

/** date 配列から domain (Date[]) を生成して x scale 共通設定を返す
 *  scale 標準 axis は無効化 (axis: null) し、 marks 側で Plot.axisX を使って毎月 tick を強制する。
 *  これは Plot v0.6 の auto thinning が ticks 明示を無視するための回避策。 */
export function buildTimeAxis(opts?: { label?: string | null; domain?: Date[] }) {
  const base = {
    label: opts?.label ?? null,
    type: "time" as const,
    axis: null,  // 標準 axis を無効化、marks に Plot.axisX を入れる
  };
  if (opts?.domain && opts.domain.length >= 2) {
    return { ...base, domain: opts.domain };
  }
  return base;
}

/** 月 tick (数字のみ) を強制的に毎月描画する axis mark
 *  buildTimeAxis(axis:null) と組み合わせて使う */
export function buildMonthAxisMark(domain: Date[] | undefined): Plot.Markish | null {
  if (!domain || domain.length < 2) return null;
  return Plot.axisX({
    ticks: buildMonthlyTicks(domain),
    tickFormat: fmtMonthJp,
    anchor: "bottom",
    fontSize: 11,
    label: null,
  });
}

/** 月 tick の下段に「年」を text mark で表示する
 *  4月 (年度初) と 1月 (西暦切替) の月初位置に小さく薄色で配置 */
export function buildYearLabelMark(domain: Date[] | undefined): Plot.Markish | null {
  if (!domain || domain.length < 2) return null;
  const ticks = buildMonthlyTicks(domain);
  const yearLabels = ticks
    .filter((d) => {
      const m = d.getUTCMonth() + 1;
      // 4月 (年度初) と 1月 (西暦切替) + domain の最初の月にだけ年表示
      const isFirst = d.getTime() === ticks[0].getTime();
      return m === 4 || m === 1 || isFirst;
    })
    .map((d) => ({ date: d, year: String(d.getUTCFullYear()) }));
  return Plot.text(yearLabels, {
    x: "date",
    y: 0,
    text: "year",
    frameAnchor: "bottom",
    textAnchor: "middle",
    dy: 28,  // axis 下、tick label のさらに下
    fontSize: 10,
    fill: "#64748b",
  });
}
